import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import PublicHeader from "@/components/layout/PublicHeader";
import PublicFooter from "@/components/layout/PublicFooter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Mail, MapPin } from "lucide-react";

export default function Contact() {
  const { t } = useTranslation();
  const [f, setF] = useState({ name: "", email: "", company: "", country: "", interest: "buyer", message: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/leads", f);
      toast.success(t("contactPage.form.success"));
      setF({ name: "", email: "", company: "", country: "", interest: "buyer", message: "" });
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2.response?.data?.detail) || t("contactPage.form.error"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="contact-page" className="min-h-screen bg-background">
      <PublicHeader />
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid grid-cols-1 lg:grid-cols-5 gap-12">
        <div className="lg:col-span-2">
          <div className="overline text-primary mb-4">{t("contactPage.eyebrow")}</div>
          <h1 className="font-heading text-4xl md:text-5xl font-black tracking-tight text-secondary leading-tight">
            {t("contactPage.h")}
          </h1>
          <p className="mt-4 text-muted-foreground leading-relaxed">{t("contactPage.sub")}</p>
          <div className="mt-10 space-y-5">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-md bg-secondary text-primary grid place-items-center">
                <Mail className="w-5 h-5" />
              </div>
              <div>
                <div className="text-sm font-semibold text-secondary">{t("contactPage.emailLabel")}</div>
                <div className="text-sm text-muted-foreground">info@safewayfinland.fi</div>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-md bg-secondary text-primary grid place-items-center">
                <MapPin className="w-5 h-5" />
              </div>
              <div>
                <div className="text-sm font-semibold text-secondary">{t("contactPage.officesLabel")}</div>
                <div className="text-sm text-muted-foreground">{t("contactPage.officesValue")}</div>
              </div>
            </div>
          </div>
        </div>
        <div className="lg:col-span-3 bg-white border border-border rounded-lg p-8">
          <form data-testid="contact-form" onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="c-name">{t("contactPage.form.name")}</Label>
              <Input id="c-name" data-testid="contact-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} required />
            </div>
            <div>
              <Label htmlFor="c-email">{t("contactPage.form.email")}</Label>
              <Input id="c-email" data-testid="contact-email" type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} required />
            </div>
            <div>
              <Label htmlFor="c-company">{t("contactPage.form.company")}</Label>
              <Input id="c-company" data-testid="contact-company" value={f.company} onChange={(e) => setF({ ...f, company: e.target.value })} />
            </div>
            <div>
              <Label htmlFor="c-country">{t("contactPage.form.country")}</Label>
              <Input id="c-country" data-testid="contact-country" value={f.country} onChange={(e) => setF({ ...f, country: e.target.value })} />
            </div>
            <div className="md:col-span-2">
              <Label htmlFor="c-interest">{t("contactPage.form.iAm")}</Label>
              <select
                id="c-interest"
                data-testid="contact-interest"
                className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                value={f.interest}
                onChange={(e) => setF({ ...f, interest: e.target.value })}
              >
                <option value="buyer">{t("lead.buyer")}</option>
                <option value="supplier">{t("lead.supplier")}</option>
                <option value="partner">{t("lead.partner")}</option>
                <option value="other">{t("lead.other")}</option>
              </select>
            </div>
            <div className="md:col-span-2">
              <Label htmlFor="c-message">{t("contactPage.form.message")}</Label>
              <Textarea id="c-message" data-testid="contact-message" rows={5} value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} required />
            </div>
            <div className="md:col-span-2">
              <Button type="submit" data-testid="contact-submit" disabled={busy}>
                {busy ? t("contactPage.form.sending") : t("contactPage.form.send")}
              </Button>
            </div>
          </form>
        </div>
      </section>
      <PublicFooter />
    </div>
  );
}
